import React, { useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatChart from './ChatChart';
import CopyBlock from './CopyBlock';
import InchatQuiz from './InchatQuiz';
import InchatFlashcards from './InchatFlashcards';
import CodeBlock, { MarkdownStreamingContext } from './CodeBlock';
import { AnswerPartBoundary, AnswerPartFallback } from './AnswerPartBoundary';
import { findFencePart, partToText } from '../../utils/chatRichCatalog';
import { inchatFenceToMarkdown } from '../../utils/chatQuizSpec';

const FALLBACK_REMARK_PLUGINS = [[remarkGfm, { singleTilde: false }]];

/** 대신 보일 글을 표·목록 모양 그대로 그린다(부품 없이 기본 마크다운만). */
export const renderFallbackMarkdown = (text) => (
  <ReactMarkdown remarkPlugins={FALLBACK_REMARK_PLUGINS}>{text}</ReactMarkdown>
);

/** 체크리스트 확인란 — 누르면 이 화면에서만 표시된다(저장하지 않음). 복사 글은 모델이 쓴 대로(□/☑). */
export const ChecklistBox = ({ checked }) => (
  <input
    aria-label="했어요 표시"
    className="wq-check__box"
    defaultChecked={Boolean(checked)}
    type="checkbox"
  />
);

/** 체크리스트 한 줄. 안에 목록이 없으면 줄 전체를 눌러도 확인란이 바뀐다. */
export const ChecklistItem = ({ node, children, className }) => {
  const nested = (node?.children || []).some((child) => ['ul', 'ol'].includes(child.tagName));
  return (
    <li className={[].concat(className || [], 'wq-check__item').join(' ')}>
      {nested ? children : <label className="wq-check__row">{children}</label>}
    </li>
  );
};

const RecordText = ({ code }) => <div className="em-record-plain-text">{code}</div>;
const Hidden = () => null;
const CardsFenceText = ({ code }) => (
  <AnswerPartFallback partId="cards_fence" renderText={renderFallbackMarkdown} text={partToText('cards_fence', { code })} />
);

// 답을 받는 중에는 풀이 화면과 같게 정답 없이 문제만.
const InchatFallbackText = ({ lang, code }) => {
  const streaming = useContext(MarkdownStreamingContext);
  const text = inchatFenceToMarkdown(lang, code, { withAnswers: !streaming });
  return text ? renderFallbackMarkdown(text) : null;
};

// 부품 id → 화면 부품. id·대신 보일 글·복사 글은 utils/chatRichCatalog.js ANSWER_PARTS 에 있다.
export const PART_COMPONENTS = {
  chart: ChatChart,
  copy: CopyBlock,
  quiz: InchatQuiz,
  flashcards: InchatFlashcards,
  record_text: RecordText,
  cards_fence: CardsFenceText,
  server_fence: Hidden,
  code: CodeBlock,
};

/** 코드 펜스 하나를 부품 목록대로 그린다. forceRecordText: 생기부 답변의 언어 없는 펜스 */
export const renderFencePart = ({ language, code, forceRecordText = false }) => {
  const part = forceRecordText ? { id: 'record_text' } : findFencePart(language);
  const Component = PART_COMPONENTS[part.id] || CodeBlock;
  const fallbackText = part.id === 'code'
    ? () => `~~~~\n${code}\n~~~~`
    : () => partToText(part.id, { code });
  const renderText = ['quiz', 'flashcards'].includes(part.id)
    ? () => <InchatFallbackText code={code} lang={part.langs[0]} />
    : renderFallbackMarkdown;
  return (
    <AnswerPartBoundary fallbackText={fallbackText} partId={part.id} renderText={renderText} resetKey={code}>
      <Component code={code} language={language} />
    </AnswerPartBoundary>
  );
};

export { AnswerPartBoundary, AnswerPartFallback };
